import { io } from 'socket.io-client';
import { SOCKET_URL } from '../config';
import { getAccessToken } from '../api/http';

/**
 * Socket.IO Service
 * Manages WebSocket connection for real-time updates
 */
class SocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 3;
    this.eventHandlers = new Map();
    this.localEventHandlers = new Map();
  }

  /**
   * Initialize socket connection with authentication
   */
  connect() {
    if (this.socket?.connected) {
      return this.socket;
    }

    const token = getAccessToken();
    if (!token) {
      console.warn('No access token available for socket connection');
      return null;
    }

    this.socket = io(SOCKET_URL, {
      auth: {
        token: token
      },
      extraHeaders: {
        Authorization: `Bearer ${token}`
      },
      transports: ['websocket', 'polling'],
      reconnection: false, // Disable auto-reconnection to prevent 429 errors
      timeout: 20000,
    });

    this.setupEventListeners();
    return this.socket;
  }

  /**
   * Setup default socket event listeners
   */
  setupEventListeners() {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      this.isConnected = true;
      this.reconnectAttempts = 0;
      this.emitLocal('connection:success', { socketId: this.socket.id });
    });

    this.socket.on('disconnect', (reason) => {
      this.isConnected = false;
      this.emitLocal('connection:disconnected', { reason });
    });

    this.socket.on('connect_error', (error) => {
      this.reconnectAttempts++;
      this.emitLocal('connection:error', { error, attempt: this.reconnectAttempts });

      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        this.disconnect();
      }
    });

    this.socket.on('error', (error) => {
      this.emitLocal('socket:error', { error });
    });

    this.socket.on('unauthorized', (error) => {
      this.emitLocal('auth:failed', { error });
      this.disconnect();
    });
  }

  /**
   * Subscribe to a doctor's queue
   */
  subscribeToQueue(doctorId) {
    if (!this.socket?.connected) {
      return;
    }

    this.socket.emit('subscribe:queue', { doctorId });

    this.socket.once('subscribed:queue', (data) => {
      this.emitLocal('queue:subscribed', data);
    });
  }

  /**
   * Unsubscribe from a doctor's queue
   */
  unsubscribeFromQueue(doctorId) {
    if (!this.socket?.connected) {
      return;
    }

    this.socket.emit('unsubscribe:queue', { doctorId });
  }

  /**
   * Listen to a socket event (from server) OR local event (connection status)
   */
  on(event, handler) {
    const localEvents = ['connection:success', 'connection:disconnected', 'connection:error', 'auth:failed', 'socket:error', 'queue:subscribed'];
    
    if (localEvents.includes(event)) {
      if (!this.localEventHandlers.has(event)) {
        this.localEventHandlers.set(event, new Set());
      }
      this.localEventHandlers.get(event).add(handler);
      return;
    }

    if (!this.socket) {
      return;
    }

    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, new Set());
    }
    this.eventHandlers.get(event).add(handler);

    this.socket.on(event, handler);
  }

  /**
   * Listen to a socket event once
   */
  once(event, handler) {
    if (!this.socket) {
      return;
    }

    this.socket.once(event, handler);
  }

  /**
   * Remove event listener
   */
  off(event, handler) {
    const localEvents = ['connection:success', 'connection:disconnected', 'connection:error', 'auth:failed', 'socket:error', 'queue:subscribed'];
    
    if (localEvents.includes(event)) {
      if (handler) {
        this.localEventHandlers.get(event)?.delete(handler);
      } else {
        this.localEventHandlers.delete(event);
      }
      return;
    }

    if (!this.socket) return;

    if (handler) {
      this.socket.off(event, handler);
      this.eventHandlers.get(event)?.delete(handler);
    } else {
      this.socket.off(event);
      this.eventHandlers.delete(event);
    }
  }

  /**
   * Emit a local event (for internal use)
   */
  emitLocal(event, data) {
    const handlers = this.localEventHandlers.get(event);
    if (handlers) {
      handlers.forEach(handler => {
        try {
          handler(data);
        } catch (error) {
          console.error(`Error in ${event} handler:`, error);
        }
      });
    }
  }

  /**
   * Emit a socket event
   */
  emit(event, data) {
    if (!this.socket?.connected) {
      return;
    }

    this.socket.emit(event, data);
  }

  /**
   * Disconnect socket
   */
  disconnect() {
    if (this.socket) {
      this.eventHandlers.forEach((handlers, event) => {
        handlers.forEach(handler => {
          this.socket.off(event, handler);
        });
      });
      this.eventHandlers.clear();

      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
    }
  }

  /**
   * Get connection status
   */
  getConnectionStatus() {
    return {
      connected: this.isConnected,
      socketId: this.socket?.id || null,
    };
  }

  /**
   * Reconnect socket with new token
   */
  reconnectWithToken() {
    this.disconnect();
    return this.connect();
  }
}

// Create singleton instance
const socketService = new SocketService();

export default socketService;
